import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Spinner,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input,
  FormGroup,
  Table,
  FormFeedback,
  Badge,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  useGetSupplierByIdQuery,
  useGetProductsListQuery,
  useCreateSupplierCostMappingMutation,
  useUpdateSupplierCostMappingMutation,
  useGetSupplierProductsByIdQuery,
  useDeleteSupplierCostMappingByIdMutation
} from "slices/InventoryManagement/inventoryManagementSlice";
import { PriceViewCompact } from "../CommonComponents/PriceViewComponent";

// Product Row Component for inline editing
interface ProductRowProps {
  product: any;
  onUpdate: (productId: number, totalAmount: string, discount: string) => Promise<void>;
  onDelete: (mappingId: number, productName: string) => Promise<void>;
  isLoading: boolean;
  isDeleting: boolean;
}

const ProductRow: React.FC<ProductRowProps> = ({
  product,
  onUpdate,
  onDelete,
  isLoading,
  isDeleting
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    total_amount: product.total_amount.toString(),
    discount: product.discount.toString()
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleEdit = () => {
    setIsEditing(true);
    setEditData({
      total_amount: product.total_amount.toString(),
      discount: product.discount.toString()
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData({
      total_amount: product.total_amount.toString(),
      discount: product.discount.toString()
    });
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      await onUpdate(product.product, editData.total_amount, editData.discount);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update product:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await onDelete(product.mapping_id, product.product_name);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Failed to delete product:', error);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const handleInputChange = (field: string, value: string) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateFinalAmount = () => {
    const total = parseFloat(editData.total_amount) || 0;
    const discount = parseFloat(editData.discount) || 0;
    const discountAmount = total * (discount / 100);
    return (total - discountAmount).toFixed(2);
  };

  const calculateDiscountAmount = () => {
    const total = parseFloat(editData.total_amount) || 0;
    const discount = parseFloat(editData.discount) || 0;
    return (total * (discount / 100)).toFixed(2);
  };

  const isSaveDisabled =
    isSubmitting ||
    !editData.total_amount ||
    isNaN(Number(editData.total_amount)) ||
    Number(editData.total_amount) < 0 ||  // Changed from == 0 to < 0
    isNaN(Number(editData.discount)) ||
    Number(editData.discount) < 0 ||
    Number(editData.discount) > 100;

  return (
    <>
      <tr>
        <td>
          <strong>{product.product_name}</strong>
          {product.brand_company && (
            <div className="text-muted small">Brand: {product.brand_company}</div>
          )}
        </td>
        <td className="text-end">
          {isEditing ? (
            <div className="d-flex align-items-center justify-content-end">
              <span className="me-2">$</span>
              <Input
                type="number"
                className="form-control form-control-sm text-end"
                value={editData.total_amount}
                onChange={(e) => handleInputChange('total_amount', e.target.value)}
                disabled={isSubmitting}
                style={{ width: '120px' }}
                min="0"  // This allows 0 and positive numbers, restricts negatives
                step="0.01"
              />
            </div>
          ) : (
            <PriceViewCompact price={parseFloat(product.total_amount).toFixed(2)}/>
          )}
        </td>
        <td className="text-end">
          {isEditing ? (
            <div className="d-flex align-items-center justify-content-end">
              <Input
                type="number"
                className="form-control form-control-sm text-end"
                value={editData.discount}
                onChange={(e) => handleInputChange('discount', e.target.value)}
                disabled={isSubmitting}
                style={{ width: '80px' }}
                min="0"
                max="100"
                step="0.1"
              />
              <span className="ms-1">%</span>
            </div>
          ) : (
            `${parseFloat(product.discount).toFixed(1)}%`
          )}
        </td>
        <td className="text-end">
          <span className="fw-bold text-success">
            {isEditing ? <PriceViewCompact price={calculateFinalAmount()} /> : <PriceViewCompact price={(parseFloat(product.total_amount) - (parseFloat(product.total_amount) * parseFloat(product.discount) / 100)).toFixed(2)} />}
          </span>
          {isEditing && (
            <div className="text-muted small">
              Discount: <PriceViewCompact price={calculateDiscountAmount()} />
            </div>
          )}
        </td>
        <td>
          <div className="d-flex gap-2 justify-content-center">
            {isEditing ? (
              <>
                <Button
                  color="success"
                  size="sm"
                  onClick={handleSave}
                  disabled={isSaveDisabled}
                >
                  {isSubmitting ? (
                    <Spinner size="sm" />
                  ) : (
                    <i className="ri-check-line"></i>
                  )}
                </Button>
                <Button
                  color="secondary"
                  size="sm"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  <i className="ri-close-line"></i>
                </Button>
              </>
            ) : (
              <>
                <Button
                  color="primary"
                  size="sm"
                  onClick={handleEdit}
                  disabled={isLoading}
                  title="Edit product"
                >
                  <i className="ri-edit-line"></i>
                </Button>
                <Button
                  color="danger"
                  size="sm"
                  onClick={handleDeleteClick}
                  disabled={isLoading || isDeleting}
                  title="Delete product"
                >
                  {isDeleting ? (
                    <Spinner size="sm" />
                  ) : (
                    <i className="ri-delete-bin-line"></i>
                  )}
                </Button>
              </>
            )}
          </div>
        </td>
      </tr>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteConfirm} toggle={handleCancelDelete} size="sm">
        <ModalHeader toggle={handleCancelDelete}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          <p className="text-center">
            Are you sure you want to remove <strong>{product.product_name}</strong> from this supplier?
          </p>
          <p className="text-muted text-center small">
            This action cannot be undone.
          </p>
        </ModalBody>
        <ModalFooter className="justify-content-center">
          <Button
            color="danger"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              'Yes, Delete'
            )}
          </Button>
          <Button
            color="secondary"
            onClick={handleCancelDelete}
            disabled={isDeleting}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

const SupplierDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const supplierId = parseInt(id || "0");

  const {
    data: supplier,
    isLoading,
    error,
  } = useGetSupplierByIdQuery(supplierId);

  // Product search and modal state
  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);

  // Get products for search using useGetProductsListQuery
  const { data: productsData, isLoading: isProductsLoading } =
    useGetProductsListQuery({ search: searchTerm || "" });

  // Get supplier products
  const {
    data: supplierProductsData,
    isLoading: isSupplierProductsLoading,
    refetch: refetchSupplierProducts,
  } = useGetSupplierProductsByIdQuery(supplierId);

  // Create supplier cost mapping mutation
  const [createSupplierCostMapping, { isLoading: isCreatingMapping }] =
    useCreateSupplierCostMappingMutation();

  // Update supplier cost mapping mutation
  const [updateSupplierCostMapping, { isLoading: isUpdatingMapping }] =
    useUpdateSupplierCostMappingMutation();

  // Delete supplier cost mapping mutation
  const [deleteSupplierCostMapping, { isLoading: isDeletingMapping }] =
    useDeleteSupplierCostMappingByIdMutation();

  // Formik for add product form
  const addProductFormik = useFormik({
    initialValues: {
      discount: "0", // Only keep discount field
    },
    validationSchema: Yup.object({
      discount: Yup.number()
        .min(0, "Discount cannot be negative")
        .max(100, "Discount cannot exceed 100%")
        .typeError("Discount must be a number"),
    }),
    onSubmit: async (values) => {
      if (selectedProducts.length === 0) {
        toast.warning("Please select at least one product", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      try {
        // Create mappings for all selected products with total_amount as 0
        const createPromises = selectedProducts.map(product =>
          createSupplierCostMapping({
            supplier: supplierId,
            product: product.id,
            total_amount: "0", // Hardcoded as 0
            discount: values.discount || "0",
          }).unwrap()
        );

        await Promise.all(createPromises);

        // Show success toast
        toast.success(`${selectedProducts.length} product(s) added to supplier successfully!`, {
          position: "top-right",
          autoClose: 3000,
        });

        // Reset form and close modal
        addProductFormik.resetForm();
        closeAddProductModal();

        // Refresh supplier products data
        refetchSupplierProducts();
      } catch (error) {
        console.error("Failed to add products to supplier:", error);
        toast.error("Failed to add products. Please try again.", {
          position: "top-right",
          autoClose: 4000,
        });
      }
    },
  });

  const handleBack = () => {
    navigate("/supplier-list");
  };

  // Product modal handlers
  const openAddProductModal = () => {
    setIsAddProductModalOpen(true);
    setSearchTerm("");
    setSelectedProducts([]);
    addProductFormik.resetForm();
  };

  const closeAddProductModal = () => {
    setIsAddProductModalOpen(false);
    setSearchTerm("");
    setSelectedProducts([]);
    addProductFormik.resetForm();
  };

  const handleProductSelect = (product: any) => {
    // Check if product is already added to supplier
    if (isProductAlreadyAdded(product.id)) {
      toast.warning(
        `${product.name} is already added to this supplier.`,
        {
          position: "top-right",
          autoClose: 3000,
        }
      );
      return;
    }

    // Check if product is already selected in current session
    if (isProductSelected(product.id)) {
      // Remove from selection
      setSelectedProducts(prev =>
        prev.filter(p => p.id !== product.id)
      );
    } else {
      // Add to selection
      setSelectedProducts(prev => [...prev, product]);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Check if product is already added to supplier
  const isProductAlreadyAdded = (productId: number) => {
    return supplierProductsData?.some(
      (item: any) => item.product === productId
    );
  };

  // Check if product is selected in current session
  const isProductSelected = (productId: number) => {
    return selectedProducts.some(p => p.id === productId);
  };

  // Handle inline product update
  const handleProductUpdate = async (productId: number, totalAmount: string, discount: string) => {
    try {
      // Find the mapping_id from the existing supplier products
      const existingProduct = supplierProductsData?.find(
        (p: any) => p.product === productId
      );

      if (existingProduct) {
        await updateSupplierCostMapping({
          id: existingProduct.mapping_id,
          values: {
            supplier: supplierId,
            product: productId,
            total_amount: totalAmount,
            discount: discount
          }
        }).unwrap();

        toast.success("Product updated successfully!", {
          position: "top-right",
          autoClose: 3000,
        });

        // Refresh supplier products data
        refetchSupplierProducts();
      }
    } catch (error) {
      console.error("Failed to update product:", error);
      toast.error("Failed to update product. Please try again.", {
        position: "top-right",
        autoClose: 4000,
      });
      throw error;
    }
  };

  // Handle product deletion
  const handleProductDelete = async (mappingId: number, productName: string) => {
    try {
      await deleteSupplierCostMapping(mappingId).unwrap();

      toast.success(`${productName} removed from supplier successfully!`, {
        position: "top-right",
        autoClose: 3000,
      });

      // Refresh supplier products data
      refetchSupplierProducts();
    } catch (error) {
      console.error("Failed to delete product:", error);
      toast.error("Failed to delete product. Please try again.", {
        position: "top-right",
        autoClose: 4000,
      });
      throw error;
    }
  };

  const productList = Array.isArray(productsData)
    ? productsData
    : productsData?.results || [];

  const availableProducts = productList.filter(
    (product: any) => !isProductAlreadyAdded(product.id)
  );


  if (isLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Supplier Details" pageTitle="Suppliers" />
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  if (error || !supplier) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Supplier Details" pageTitle="Suppliers" />
          <Alert color="danger">Supplier not found or failed to load.</Alert>
          <Button color="primary" onClick={handleBack}>
            Back to Suppliers
          </Button>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Supplier Details" pageTitle="Suppliers" />

        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                {/* View Mode Only */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 className="card-title mb-0">{supplier.name}</h4>
                </div>

                <Row className="mt-4">
                  <Col md={6}>
                    <h6 className="text-muted">Basic Information</h6>
                    <table className="table table-borderless">
                      <tbody>
                        <tr>
                          <td width="40%" className="fw-medium">
                            Registration Number:
                          </td>
                          <td>{supplier.registration_number || "—"}</td>
                        </tr>
                        <tr>
                          <td className="fw-medium">Tax ID:</td>
                          <td>{supplier.tax_id || "—"}</td>
                        </tr>
                        <tr>
                          <td className="fw-medium">Business Type:</td>
                          <td>{supplier.business_type || "—"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </Col>

                  <Col md={6}>
                    <h6 className="text-muted">Contact Information</h6>
                    <table className="table table-borderless">
                      <tbody>
                        <tr>
                          <td width="40%" className="fw-medium">
                            Contact Name:
                          </td>
                          <td>{supplier.contact_name || "—"}</td>
                        </tr>
                        <tr>
                          <td className="fw-medium">Email:</td>
                          <td>
                            {supplier.contact_email ? (
                              <a href={`mailto:${supplier.contact_email}`}>
                                {supplier.contact_email}
                              </a>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className="fw-medium">Phone:</td>
                          <td>{supplier.contact_phone || "—"}</td>
                        </tr>
                        <tr>
                          <td className="fw-medium">Website:</td>
                          <td>
                            {supplier.website ? (
                              <a
                                href={supplier.website}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {supplier.website}
                              </a>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </Col>
                </Row>

                {/* Display Supplier Products */}
                <Row className="mt-4">
                  <Col md={12}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="text-muted mb-0">Supplier Products</h6>
                      <Button
                        color="success"
                        size="sm"
                        onClick={openAddProductModal}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>
                        Add Products
                      </Button>
                    </div>

                    {isSupplierProductsLoading ? (
                      <div className="text-center py-3">
                        <Spinner color="primary" size="sm" />
                        <span className="ms-2">Loading products...</span>
                      </div>
                    ) : supplierProductsData &&
                      supplierProductsData.length > 0 ? (
                      <div className="table-responsive">
                        <Table striped responsive className="table-bordered table-nowrap align-middle mb-0" >
                          <thead className="table-light">
                            <tr>
                              <th>Product Name</th>
                              <th className="text-end">Total Amount</th>
                              <th className="text-end">Discount</th>
                              <th className="text-end">Final Amount</th>
                              <th style={{ width: '150px' }} className="text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {supplierProductsData.map((product: any) => (
                              <ProductRow
                                key={product.mapping_id}
                                product={product}
                                onUpdate={handleProductUpdate}
                                onDelete={handleProductDelete}
                                isLoading={isUpdatingMapping}
                                isDeleting={isDeletingMapping}
                              />
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <Alert color="info" className="text-center">
                        No products added to this supplier yet.
                      </Alert>
                    )}
                  </Col>
                </Row>

                <div className="mt-4">
                  <Button color="secondary" onClick={handleBack}>
                    <i className="ri-arrow-left-line align-bottom me-1"></i>
                    Back to Suppliers
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Add Product Modal */}
        <Modal
          isOpen={isAddProductModalOpen}
          toggle={closeAddProductModal}
          size="xl"
        >
          <ModalHeader toggle={closeAddProductModal}>
            Add Products to Supplier
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label for="productSearch">Search Products</Label>
                  <Input
                    type="text"
                    id="productSearch"
                    placeholder="Search products by name or brand..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                </FormGroup>
              </Col>
            </Row>

            {/* Selected Products Summary */}
            {selectedProducts.length > 0 && (
              <Row className="mt-3">
                <Col md={12}>
                  <div className="alert alert-light border">
                    <strong>Selected Products:</strong> {selectedProducts.length}
                    <div className="mt-2">
                      {selectedProducts.map(product => (
                        <Badge
                          key={product.id}
                          color="secondary"
                          className="me-2 mb-2"
                        >
                          {product.name}
                          <i
                            className="ri-close-line ms-1 cursor-pointer"
                            onClick={() => handleProductSelect(product)}
                            style={{ cursor: 'pointer' }}
                          ></i>
                        </Badge>
                      ))}
                    </div>
                  </div>
                </Col>
              </Row>
            )}

            {/* Product List - Show only available products */}
            <Row className="mt-3">
              <Col md={12}>
                <h6>Available Products</h6>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <small className="text-muted">
                    Showing {availableProducts.length} available products
                    {productsData && productsData.length > availableProducts.length && (
                      <span className="ms-2">
                        ({productsData.length - availableProducts.length} already added to supplier)
                      </span>
                    )}
                  </small>
                </div>
                <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                  {isProductsLoading ? (
                    <div className="text-center p-3">
                      <Spinner color="primary" size="sm" />
                      <span className="ms-2">Loading products...</span>
                    </div>
                  ) : availableProducts.length > 0 ? (
                    <div className="list-group">
                      {availableProducts.map((product: any) => {
                        const isSelected = isProductSelected(product.id);

                        return (
                          <div
                            key={product.id}
                            className={`list-group-item list-group-item-action ${isSelected ? "bg-light text-dark border-secondary" : ""
                              }`}
                            onClick={() => handleProductSelect(product)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <strong className={isSelected ? "text-dark" : ""}>
                                  {product.name}
                                </strong>
                                {product.brand_company && (
                                  <span className={`ms-2 ${isSelected ? "text-dark" : "text-muted"}`}>
                                    (Brand: {product.brand_company})
                                  </span>
                                )}
                              </div>
                              <div>
                                {isSelected ? (
                                  <i className="ri-checkbox-circle-fill text-secondary"></i>
                                ) : (
                                  <i className="ri-checkbox-blank-circle-line text-muted"></i>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center p-3 text-muted">
                      {searchTerm ? (
                        "No available products found matching your search"
                      ) : (
                        <>
                          <i className="ri-information-line fs-4 mb-2 d-block"></i>
                          <div>All products are already added to this supplier.</div>
                          <small>Remove products from the table above to make them available again.</small>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </Col>
            </Row>

            {/* Cost and Discount Inputs */}
            {/* {selectedProducts.length > 0 && (
              <Row className="mt-4">
                <Col md={6}>
                  <FormGroup>
                    <Label for="total_amount">Total Amount ($)</Label>
                    <Input
                      type="number"
                      id="total_amount"
                      name="total_amount"
                      placeholder="Enter total amount"
                      value={addProductFormik.values.total_amount}
                      onChange={addProductFormik.handleChange}
                      onBlur={addProductFormik.handleBlur}
                      invalid={
                        addProductFormik.touched.total_amount &&
                        Boolean(addProductFormik.errors.total_amount)
                      }
                      min="0"
                      step="0.01"
                      disabled={isCreatingMapping}
                    />
                    <FormFeedback>
                      {addProductFormik.errors.total_amount}
                    </FormFeedback>
                    <div className="form-text">
                      This amount will be applied to all selected products
                    </div>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="discount">Discount (%)</Label>
                    <Input
                      type="number"
                      id="discount"
                      name="discount"
                      placeholder="Enter discount percentage"
                      value={addProductFormik.values.discount}
                      onChange={addProductFormik.handleChange}
                      onBlur={addProductFormik.handleBlur}
                      invalid={
                        addProductFormik.touched.discount &&
                        Boolean(addProductFormik.errors.discount)
                      }
                      min="0"
                      max="100"
                      step="0.1"
                      disabled={isCreatingMapping}
                    />
                    <FormFeedback>
                      {addProductFormik.errors.discount}
                    </FormFeedback>
                    <div className="form-text">
                      This discount percentage will be applied to all selected products
                    </div>
                  </FormGroup>
                </Col>
              </Row>
            )} */}
          </ModalBody>
          <ModalFooter>
            <Button
              color="success"
              onClick={() => addProductFormik.handleSubmit()}
              disabled={
                selectedProducts.length === 0 ||
                !addProductFormik.isValid ||
                isCreatingMapping
              }
            >
              {isCreatingMapping ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Adding Products...
                </>
              ) : (
                `Add ${selectedProducts.length} Product(s)`
              )}
            </Button>
            <Button
              color="secondary"
              onClick={closeAddProductModal}
              disabled={isCreatingMapping}
            >
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
      <ToastContainer />
    </div>
  );
};

export default SupplierDetailsPage;